import router from '@/router';
import { Bid } from '@/types/app';
import { useStore } from '@/store';
import { useGapi } from '@/services';
import { formatNum } from '@/utils/route-utilities';

export const exportToDrive = async (bidData: Bid) => {
  const templateConstants = [
    '{{CLIENT_COMPANY}}',
    '{{COMPANY_ADDRESS}}',
    '{{COMPANY_PHONE}}',
    '{{FOR}}',
    '{{JOB_NAME}}',
    '{{LOCATION}}',
    '{{DATE}}',
    '{{COMPLETION_DATE}}',
    '{{CONTACT}}',
    '{{NOTES}}'
  ];

  const { verifyAccessToken } = useGapi();
  await verifyAccessToken();

  const store = useStore();
  const { accessToken } = store.state.auth;

  let bidNote = bidData.notes;

  if (!bidData.notes) {
    bidNote = ' ';
  }

  const tableConstants = bidData.materialList.map(material => {
    const priceAfterTax = material.pricePerUnit.number;
    const tax = parseFloat(material.taxValue ?? '0') / material.quantity.number;
    const priceBeforeTax = priceAfterTax - tax;

    const isLegacyMaterial = !material.taxValue;
    const taxValue = isLegacyMaterial ? '-' : `$${formatNum(tax)}/${material.pricePerUnit.metric}`;

    return [
      material.materialDisplayName,
      `${material.quantity.number} ${material.quantity.metric}`,
      material.truckDisplayName,
      `$${formatNum(priceBeforeTax)}/${material.pricePerUnit.metric}`,
      taxValue,
      `$${formatNum(priceAfterTax)}/${material.pricePerUnit.metric}`
    ];
  });

  const bidConstants = [
    bidData.groupCompanyName,
    bidData.groupCompanyAddress,
    bidData.groupCompanyPhone,
    bidData.client.name,
    bidData.jobName,
    bidData.jobLocation,
    bidData.date.toDate().toLocaleDateString(),
    bidData.completionDate.toDate().toLocaleDateString(),
    bidData.client.email,
    bidNote
  ];

  const replaceRequests = new Array(templateConstants.length);

  for (let i = 0; i < templateConstants.length; i += 1) {
    replaceRequests[i] = {
      replaceAllText: {
        containsText: {
          text: templateConstants[i],
          matchCase: true
        },
        replaceText: bidConstants[i]
      }
    };
  }

  const invoiceName = bidData.client.name.concat('_', bidData.jobName, '_', bidData.date.toDate().toLocaleDateString());

  const mergeText = async (doc: gapi.client.drive.File) => {
    if (doc.id) {
      await gapi.client.docs.documents.batchUpdate({
        access_token: accessToken,
        documentId: doc.id,
        resource: {
          requests: replaceRequests
        }
      });
    }
  };

  const createInsertTableRowRequest = (tableIndex: number, rowNumber: number) => {
    return {
      insertTableRow: {
        tableCellLocation: {
          tableStartLocation: {
            index: tableIndex
          },
          rowIndex: rowNumber,
          columnIndex: 1
        },
        insertBelow: true
      }
    };
  };

  const createInsertTextRequest = (textIndex: number, textString: string) => {
    return {
      insertText: {
        location: {
          index: textIndex
        },
        text: textString
      }
    };
  };

  const fillTable = async (doc: gapi.client.drive.File) => {
    if (!doc.id) return;

    for (let i = 0; i < bidData.materialList.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const currentVersion = await gapi.client.docs.documents.get({
        access_token: accessToken,
        documentId: doc.id,
        fields: 'body/content(startIndex,table)'
      });

      const invoiceTable = currentVersion.result.body?.content?.filter(item => item.table)[1];
      const tableStartIndex = invoiceTable?.startIndex || 0;
      const tableRows = invoiceTable?.table?.tableRows || [];

      let tableLengths = 2;
      for (let j = 0; j < tableConstants[i].length; j += 1) {
        let cellIndex = tableRows[i + 1].startIndex;
        if (typeof cellIndex === 'number') cellIndex += tableLengths;

        const shouldInsertRow = j === tableConstants[i].length - 1 && i !== bidData.materialList.length - 1;
        const tableRowRequest = shouldInsertRow ? [createInsertTableRowRequest(tableStartIndex, tableRows.length - 1)] : [];

        const tableResource =
          typeof cellIndex === 'number'
            ? {
                requests: [createInsertTextRequest(cellIndex, tableConstants[i][j]), ...tableRowRequest]
              }
            : {};

        // eslint-disable-next-line no-await-in-loop
        await gapi.client.docs.documents.batchUpdate({
          access_token: accessToken,
          documentId: doc.id,
          resource: tableResource
        });
        tableLengths += tableConstants[i][j].length + 2;
      }
    }
  };

  const userId = store.state.auth.firebaseUser?.uid;
  const userSettings = store.getters.userSettings.get.find(setting => setting.userId === userId);

  if (!userSettings || !userSettings.templateId) {
    router.push('/settings/user-settings');
    return;
  }

  const { folderId, templateId } = userSettings;

  if (!folderId) {
    const newFolder = await gapi.client.drive.files.create({
      access_token: accessToken,
      resource: {
        name: 'Aggbid Bids',
        mimeType: 'application/vnd.google-apps.folder'
      }
    });

    if (!newFolder.result.id) return;

    await store.dispatch.userSettings.update({
      ...userSettings,
      folderId: newFolder.result.id
    });
  }

  gapi.client.drive.files
    .copy({
      access_token: accessToken,
      fileId: templateId,
      resource: {
        name: invoiceName,
        parents: [folderId]
      }
    })
    .then(async response => {
      const doc = response.result;
      await mergeText(doc);
      await fillTable(doc);
      let googleUrl = 'https://docs.google.com/document/d/';
      if (doc.id) googleUrl = googleUrl.concat(doc.id, '/edit');
      window.open(googleUrl);
    });
};
